import { useQueryClient } from '@tanstack/react-query';

import { readShigoAppConfig } from '../../../../../lib/config';
import { useDemandClient } from '../../../../agency/useDemandClient';
import type { AssignedAgency } from '../../../../auth/agencyAccess';
import { agenciesQueryKey, useAgencyClient } from '../../../../supply/agency/hooks';
import { brandsQueryKey } from '../../hooks';
import type { Brand, BrandPayload } from '../../model';
import { agencyDemandQueryPrefix, preserveBrandAgency } from './brandEditorHelpers';

/**
 * Save handler for admins and agency workspaces: writes through the demand API, assigns a
 * newly chosen agency (admin only), then refreshes brand lists and options.
 */
export function useBrandEditorSave({
  brand,
  scopeAgencyId,
  assignedAgency,
}: {
  brand: Brand | null;
  scopeAgencyId: string | undefined;
  assignedAgency: AssignedAgency | undefined;
}) {
  const { networkId } = readShigoAppConfig();
  const agencyClient = useAgencyClient();
  const demand = useDemandClient(scopeAgencyId);
  const queryClient = useQueryClient();
  const prefix = agencyDemandQueryPrefix(networkId, scopeAgencyId);

  async function updateBrand(
    current: Brand,
    agencyId: string,
    payload: BrandPayload,
    logo?: File | null,
  ) {
    const updated = await demand.brands.update(current.id, payload, logo);
    queryClient.setQueryData(
      [...prefix, ...brandsQueryKey, 'detail', current.id],
      preserveBrandAgency(updated, current),
    );
    if (!scopeAgencyId && agencyId && !assignedAgency) {
      await agencyClient.assignBrand(agencyId, current.id);
      void queryClient.invalidateQueries({
        queryKey: [...brandsQueryKey, 'detail', current.id],
      });
    }
  }

  return async (agencyId: string, payload: BrandPayload, logo?: File | null) => {
    if (brand) await updateBrand(brand, agencyId, payload, logo);
    else await demand.brands.create(agencyId, payload, logo);
    void queryClient.invalidateQueries({ queryKey: [...prefix, 'campaigns', 'brands', 'list'] });
    void queryClient.invalidateQueries({ queryKey: [...prefix, 'campaigns', 'brand-options'] });
    if (!scopeAgencyId)
      void queryClient.invalidateQueries({
        queryKey: [...agenciesQueryKey, networkId, 'brand-assignments'],
      });
  };
}
